import { FormService, Logger } from '@core/services';
import { GroupEntity, GroupParticipant, SubTaskAssignedTo, SubTaskEntity, TagEntity, TaskCollaborator, TaskEntity, UserEntity } from '@core/entities';
import { BreadcrumbItem, CategorizedTasksType } from '@core/types';
import { UseClub, UseGroups, UseTask } from '@core/usecases';
import { ChangeDetectorRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { DateTimeUtility, EnumToKeyValueArray, GetTeamId, GetUserId, isMobileView, stripHtml } from '@core/utils';
import { ConfirmationDialogType, DeleteConfirmationOptions, NotificationType, SectionType, SocketEvents, SubtaskStatus, TasksTab, TaskStatus, TaskType, UpdateTabType } from '@core/enums';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { HotToastService } from '@ngxpert/hot-toast';
import { ConfirmationDialogComponent, SingleInputCreateComponent } from '@web/_shared/dialogs';
import { DialogService } from '@ngneat/dialog';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { DropDownItem } from '@core/types/Dropdown.type';
import { instanceToPlain, plainToClass } from 'class-transformer';
import { Router } from '@angular/router';
import { PERMISSIONS, PermissionService } from '@auth';
import { SocketIoService } from '@core/socket-io';
import { ButtonComponent } from '@shared/components/ui';

const log = new Logger('Tasks Composition');

export class TasksComposition extends FormService {
  breadcrumbs: BreadcrumbItem[] = [];
  currentTabRoute = '';
  currentTask: TaskEntity;
  currentTaskStatus: TaskStatus;
  currentSubtasks: SubTaskEntity[];

  selectedImage: File;
  individualOptions: DropDownItem[] = [];
  tags: TagEntity[] = [];
  groups: Partial<GroupEntity>[] = [];
  users: Partial<UserEntity>[] = [];
  taskParticipants: Partial<UserEntity>[] = [];
  tabs: { label: string; displayName?: string; routerLink?: string; count?: number }[] = [];

  // This CategorizedTasks is used in the template
  categorizedTasks: CategorizedTasksType = {} as CategorizedTasksType;
  taskTypes;

  // These below 4 properties are used to show admin the changes requested by the user
  originalRecord: TaskEntity;
  updatedRecord: TaskEntity;
  updateSelectionTabs: { label: string; displayName: string }[] = [];
  currentUpdatedRecordTab = UpdateTabType.CURRENT;

  protected readonly _bottomSheetService = inject(NgxBottomSheetService);
  protected readonly _dialogService = inject(DialogService);
  protected readonly _translateService = inject(TranslateService);
  protected readonly _router = inject(Router);
  protected readonly _toast = inject(HotToastService);
  protected readonly _permissionService = inject(PermissionService);
  protected readonly _cdr = inject(ChangeDetectorRef);
  protected readonly _socketService = inject(SocketIoService);
  protected readonly _useClub = new UseClub();

  private [TasksTab.AllTasks]: TaskEntity[] = [];
  private [TasksTab.MyTasks]: TaskEntity[] = [];
  private [TasksTab.GroupTasks]: TaskEntity[] = [];
  private [TasksTab.AssignedTasks]: TaskEntity[] = [];
  private _groupParticipants: GroupParticipant[] = [];

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _useTask = new UseTask();
  private readonly _useGroup = new UseGroups();
  private readonly _currentUserId = +GetUserId();

  constructor() {
    super();
    this.tabs = [
      { label: TasksTab.AllTasks, displayName: this._translateService.instant('All tasks'), count: 0 },
      { label: TasksTab.MyTasks, displayName: this._translateService.instant('My tasks'), count: 0 },
      { label: TasksTab.GroupTasks, displayName: this._translateService.instant('Group tasks'), count: 0 },
      { label: TasksTab.AssignedTasks, displayName: this._translateService.instant('Assigned'), count: 0 },
    ];
    this.taskTypes = EnumToKeyValueArray(TaskType, this._translateService, false);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- Getters & Get Methods --------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  get taskCreatePermission(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_TASK);
  }

  get subtaskCreatePermission(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_SUBTASK);
  }

  get subTaskFormArray(): FormArray {
    return this.form.get('subtasks') as FormArray;
  }

  get isUserAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  get isUserAuthor(): boolean {
    return +this.currentTask?.organizerId === +this._currentUserId;
  }

  get isUserParticipant(): boolean {
    return this.taskParticipants.some((participant) => participant.id === this._currentUserId);
  }

  get showUpdatedRecord(): boolean {
    return this.originalRecord?.updatedRecord && !(this.originalRecord?.denyById && this.originalRecord?.denyByUser);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------------ Action Methods -----------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  onUpdateRecordTabSelection(tabName: UpdateTabType | string) {
    switch (tabName) {
      case UpdateTabType.CURRENT:
        this.currentTask = this.originalRecord;
        this.taskParticipants = this.originalRecord.collaborators as unknown as Partial<UserEntity[]>;
        this.currentUpdatedRecordTab = UpdateTabType.CURRENT;
        break;
      case UpdateTabType.UPDATE:
        this.currentTask = this.updatedRecord;
        this.taskParticipants = this.updatedRecord.collaborators as unknown as Partial<UserEntity[]>;
        this.currentUpdatedRecordTab = UpdateTabType.UPDATE;
        break;
      default:
        break;
    }
  }

  tabChange(tabName: string) {
    const filteredTasks = this[tabName as TasksTab];
    this.categorizedTasks = this._categorizeTasks(filteredTasks);
  }

  deleteTask(task: TaskEntity) {
    if (!task.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('Task'),
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.call(this._useTask.deleteTask(task.id))
                  .then((res) => {
                    if (res) {
                      this._toast.success(this._translateService.instant('Delete Successful'));
                      this._socketService.emit(SocketEvents.SendNotification, NotificationType.Task);
                      this._bottomSheetService.closeAll();
                      // this._reloadCurrentRoute();
                    } else {
                      log.error('Error while deleting Task');
                    }
                  })
                  .catch((err) => {
                    log.error('Error while deleting Task', err);
                  });
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          closeButton: false,
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: task.title,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (res) => {
            if (res) {
              this.call(this._useTask.deleteTask(task.id))
                .then((res) => {
                  if (res) {
                    this._toast.success(this._translateService.instant('Delete Successful'));
                    this._socketService.emit(SocketEvents.SendNotification, NotificationType.Task);
                    this._dialogService.closeAll();
                    // this._reloadCurrentRoute();
                  } else {
                    log.error('Error while deleting Task');
                  }
                })
                .catch((err) => {
                  log.error('Error while deleting Task', err);
                });
            }
          },
        });
    }
  }

  onImageSelection(image: string | File) {
    this.selectedImage = image as File;
  }

  saveTask(subTaskData: SubTaskEntity[], btnRef?: ButtonComponent) {
    const form = this.form.value;
    form.organizerId = +GetUserId();
    form.teamId = +GetTeamId();

    // Loading data from form to mutate fields where required
    const data = this.removeNullObjects(instanceToPlain(new TaskEntity(this.removeEventFields(form))));
    data.status = this.currentTask ? `${this.currentTask?.status}` : '1';

    data.collaborators = this.users.filter((user) => data.collaborators.includes(user.id)).map((user) => ({ user_id: user.id, approved_status: 1 }));

    if (this.form.get('type').value === TaskType.Group) {
      data.collaborators = this._groupParticipants.map((user) => ({ user_id: user.userId, approved_status: 1 }));
    }

    // check if current user is in the collaborators list and add if not
    // if (!data.collaborators.some((collaborator: any) => collaborator.user_id === this._currentUserId)) {
    //   data.collaborators.push({ user_id: this._currentUserId, approved_status: 1 });
    // }

    // Converting data to FormData
    const body = this.jsonToFromData(this.removeEventFields(data), ['subtasks', 'tags', 'task_tags', 'collaborators']);

    body.delete('course_id');
    body.delete('event_id');
    body.delete('subtasks');
    body.delete('created_by');
    body.delete('team_id');

    if (!this._permissionService.isAdmin()) {
      body.delete('approved_status');
    }

    // Appending extra data to the FormData
    body.append('subtasks', JSON.stringify(subTaskData || []));
    body.append('team_id', GetTeamId());

    if (this.selectedImage) {
      body.append('file', this.selectedImage);
    }

    const service$ = this.currentTask ? this._useTask.updateTask(body, this.currentTask.id) : this._useTask.createNewTask(body);

    this.call(service$, btnRef)
      .then((task) => {
        if (!this._permissionService.isAdmin() && form.type === TaskType.Individual) {
          this._toast.success(this._translateService.instant('Your current Request is waiting for the Approval by Admin'), { duration: 6000 });
        } else {
          const msg = this.currentTask ? 'Update Successful' : 'Created Successfully';
          this._toast.success(this._translateService.instant(msg));
        }

        this._dialogService?.closeAll();
        this._bottomSheetService?.closeAll();
        this._socketService.emit(SocketEvents.SendNotification, NotificationType.Task);
        // this._reloadCurrentRoute();
      })
      .catch((error) => {
        log.error('Error creating task: ', error);
      });
  }

  markTaskAsComplete(event?: any, refresh = true) {
    // Check if use is admin or organizer, and if not show error message
    if (!this.isUserAdmin && !this.isUserAuthor && !this.isUserParticipant) {
      this._toast.error(this._translateService.instant('This Task Only Completed by Admin or Organizer of this Task'));
      if (event) event.target.checked = false;
      return;
    }

    // Check if all subtasks are completed before marking the task as completed
    if (this.currentTask.subtasks.some((subtask) => +subtask.status === SubtaskStatus.Incomplete)) {
      this._toast.error(this._translateService.instant('Please complete all subtasks before marking the task as completed'));
      if (event) event.target.checked = false;
      return;
    }

    // check if task is already completed and return
    if (this.currentTask.status === SubtaskStatus.Complete) return;

    const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      data: {
        message: this._translateService.instant('Are you sure you want to complete this task'),
      },
    });
    confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useTask
            .markTaskAsComplete(this.currentTask.id)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (response) => {
                if (response) {
                  this.currentTask.status = SubtaskStatus.Complete;
                  this.currentTaskStatus = TaskStatus.Completed;
                  if (event) event.target.checked = true;
                }
                if (refresh) this._reloadCurrentRoute();
              },
            });
        } else {
          if (event) event.target.checked = false;
        }
      },
    });
  }

  markSubtaskAsComplete(subtask: SubTaskEntity, event: any) {
    // Check if subtask is already completed and return
    if (+subtask.status === SubtaskStatus.Complete) return;

    // Check if user is task participant,  admin or organizer, and if not show error message
    if ((!this.isUserAdmin || !this.isUserAuthor) && !this.isUserParticipant) {
      this._toast.error(
        this._translateService.instant(
          'You do not have permission to complete this subtask, only participants of this Task & Subtasks, organizer of this task, and Administrator can complete this subtask',
        ),
      );
      event.target.checked = false;
      return;
    }

    const confirmationDelete = this._dialogService.open(ConfirmationDialogComponent, {
      size: 'md',
      closeButton: false,
      windowClass: 'dialog-overflow-unset',
      data: {
        message: this._translateService.instant('Are you sure you want to complete this'),
        emphasize: this._translateService.instant('Subtask'),
      },
    });
    confirmationDelete.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useTask
            .markSubtaskAsComplete(subtask.id)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (response) => {
                if (response) {
                  subtask.status = SubtaskStatus.Complete;
                  event.target.checked = true;
                  this._reloadCurrentRoute();
                  if (this.currentTask.status === SubtaskStatus.Incomplete) {
                    this.currentTaskStatus = TaskStatus.InProgress;
                    this._cdr.detectChanges();
                  }
                }
              },
            });
        } else {
          event.target.checked = false;
        }
      },
    });
  }

  denyTask(currentTask: TaskEntity, btnRef?: ButtonComponent) {
    btnRef?.showLoading();
    const confirmUnApprove = this._dialogService.open(SingleInputCreateComponent, {
      closeButton: false,
      data: {
        title: this._translateService.instant('Are you sure you want to unapprove this') + ' ' + this._translateService.instant('Task') + '?',
        label: this._translateService.instant('Message'),
        inputType: 'textArea',
      },
    });

    confirmUnApprove.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          this._useTask
            .denyTaskByAdmin(this.currentTask.id, result)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: () => {
                this._toast.success(this._translateService.instant('Rejected Successfully'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Task);
                this._dialogService.closeAll();
                btnRef?.showIdle();
              },
              error: (error) => {
                log.error('Error denying task: ', error);
                // this._toast.error(this.error);
                btnRef?.showError();
              },
            });
        } else {
          btnRef?.showIdle();
        }
      },
    });
  }

  approveTask(currentTask: TaskEntity, isTaskUpdate: boolean, btnRef?: ButtonComponent) {
    btnRef?.showLoading();

    this._dialogService
      .open(ConfirmationDialogComponent, {
        closeButton: false,
        data: {
          category: SectionType.TASK,
          type: ConfirmationDialogType.APPROVE,
          // emphasize: currentTask.title,
        },
      })
      .afterClosed$.subscribe({
        next: (confirmed) => {
          if (confirmed) {
            const service$ = isTaskUpdate ? this._useTask.approveTaskUpdateByAdmin(currentTask.id) : this._useTask.approveTaskByAdmin(currentTask.id);

            service$.pipe(untilDestroyed(this)).subscribe({
              next: () => {
                this._toast.success(this._translateService.instant('Approved Successfully'));
                this._socketService.emit(SocketEvents.SendNotification, NotificationType.Task);
                btnRef?.showIdle();
                this._dialogService.closeAll();
              },
              error: (error) => {
                log.error('Error approving task: ', error);
                // this._toast.error(this.error);
                btnRef?.showError();
              },
            });
          } else {
            btnRef?.showIdle();
          }
        },
      });
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected _fetchTaskById(taskId: number) {
    this.call(this._useTask.getTaskById(taskId))
      .then((task) => {
        this.currentTask = task;
        this.currentSubtasks = task.subtasks;
        this._patchForm(task);
      })
      .catch((error) => {
        log.error('Error fetching task: ', error);
        this._toast.error(this.error);
        this.form?.disable();
        if (isMobileView()) {
          this._router.navigate(['/m/tasks']);
        }
      });
  }

  protected _fetchTaskByIdReturnPromise(taskId: number) {
    return this.call(this._useTask.getTaskById(taskId));
  }

  protected initForm() {
    this.form = this._fb.group({
      title: [null, [Validators.required]],
      description: [null, [Validators.required]],
      date: [null, [Validators.required]],
      tags: [null],
      collaborators: [[], [Validators.required]],
      groupId: [null, [Validators.required]],
      type: [null, [Validators.required]],
      image: [null],
      status: ['1'],
      subtasks: this._fb.array([]),
    });

    this.form
      .get('type')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          const groupIdControl = this.form.get('groupId');
          const collaboratorsControl = this.form.get('collaborators');

          if (value === TaskType.Group) {
            collaboratorsControl.clearValidators();
            groupIdControl.setValidators([Validators.required]);
          } else if (value === TaskType.Individual) {
            groupIdControl.clearValidators();
            collaboratorsControl.setValidators([Validators.required]);
          }
          collaboratorsControl.setValue([]);
          groupIdControl.setValue(null);
          this.form.get('groupId').updateValueAndValidity();
          this.form.get('collaborators').updateValueAndValidity();
          this.form.updateValueAndValidity();
        },
      });

    this.form
      .get('groupId')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          if (value) {
            this._fetchGroupParticipants(value);
          }
        },
      });
  }

  protected fetchDataForForm() {
    this._fetchTaskTags();
    this._fetchTeamUsersAndGroups();
  }

  protected _fetchTasks() {
    this._useTask
      .getAllApprovedTasks()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (tasks) => {
          this._useClub.getClubMembers().then((resp) => {
            this.users = resp;
            tasks?.forEach((task: TaskEntity) => {
              task.usersTask = this.users.find((u) => u.id === task.organizerId);
            });
          });

          this[TasksTab.AllTasks] = (this.removeNullObjects(tasks) as TaskEntity[]).sort((a, b) => +b.id - +a.id);
          this.categorizedTasks = this._categorizeTasks(this[TasksTab.AllTasks]);
          this._updateData();

          this._markTasksSectionAsRead();
        },
        error: (error) => {
          log.error('Error fetching tasks: ', error);
          this.isLoading = false;
        },
      });
  }

  protected _fetchCourseTaskCollaborators() {
    this.isLoading = true;
    this.users = [];
    this.taskParticipants = [];
    this._useTask
      .getTaskCollaborators(this.currentTask.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (participants: UserEntity[]) => {
          if (participants && participants.length) {
            const participantsCopy = [...participants];
            this.users = participantsCopy;
            this.taskParticipants = [...participantsCopy];
            if (this.originalRecord && this.originalRecord.id) {
              this.originalRecord.collaborators = [...this.taskParticipants] as unknown as TaskCollaborator[]; // explicitly casting to TaskCollaborator[]
            }
          }

          if (this.currentTask?.title) this.isLoading = false;
        },
      });
  }

  protected _fetchTeamUsersAndUpdateSubtasksParticipants() {
    this._useClub
      .getClubMembers()
      .then((resp) => {
        this.users = { ...resp };

        // Update subtasks participants with team members for each subtask
        // used in the form to display the assignedTo users
        this.currentSubtasks.forEach((subtask: SubTaskEntity) => {
          if (subtask.assignedTo) {
            // Check if assignedTo is a string and parse it if necessary
            if (typeof subtask.assignedTo === 'string') {
              try {
                subtask.assignedTo = JSON.parse(subtask.assignedTo);
              } catch (error) {
                console.error('Error parsing assignedTo:', error);
                subtask.assignedTo = [];
              }
            }

            // Ensure assignedTo is an array
            if (Array.isArray(subtask.assignedTo)) {
              subtask.assignedTo = subtask.assignedTo.map((user: SubTaskAssignedTo) => {
                user = plainToClass(SubTaskAssignedTo, user);
                const teamUser = resp.find((u) => u.id === user.userId);
                if (teamUser) {
                  user.memberId = +teamUser.memberId;
                }
                return user;
              });
              this._cdr.detectChanges();
            } else {
              log.error('assignedTo is not an array:', subtask.assignedTo);
            }
          }
        });

        // assigning the currentSubtasks to the originalRecord and currentTask
        // to keep the data in sync after updating code with updated record logic
        this.originalRecord.subtasks = [...this.currentSubtasks];
        this.currentTask.subtasks = [...this.currentSubtasks];

        if (this.updatedRecord) {
          if (this.updatedRecord.collaborators && this.updatedRecord.collaborators.length) {
            this.updatedRecord.collaborators = this.updatedRecord.collaborators.map((collaborator: any) => {
              const user = resp.find((u) => u.id === collaborator.userId);
              if (user) {
                collaborator = user;
              }
              return collaborator;
            });
          }

          if (this.updatedRecord?.subtasks) {
            this.updatedRecord?.subtasks.forEach((subtask: SubTaskEntity) => {
              if (subtask.assignedTo) {
                if (Array.isArray(subtask.assignedTo)) {
                  subtask.assignedTo = subtask.assignedTo.map((user: SubTaskAssignedTo) => {
                    const teamUser = resp.find((u) => u.id === user.userId);
                    if (teamUser) {
                      user.memberId = +teamUser.memberId;
                    }
                    return user;
                  });
                  this._cdr.detectChanges();
                } else {
                  log.error('assignedTo is not an array:', subtask.assignedTo);
                }
              }
            });
          }
        }

        this.isLoading = false;
      })
      .catch((error) => {
        console.error('Error fetching club members:', error);
      });
  }

  protected _patchSubTasks(subtasks: SubTaskEntity[]) {
    this.subTaskFormArray.clear();

    if (!subtasks || subtasks.length === 0) {
      return;
    }
    let userIds: any[] = [];
    subtasks.forEach((subtask) => {
      if (subtask.assignedTo && typeof subtask.assignedTo === 'string') {
        subtask.assignedTo = JSON.parse(subtask.assignedTo as any);
      } else if (!subtask.assignedTo && subtask['assigned_to']) {
        subtask.assignedTo = subtask['assigned_to'];
      }

      subtask.assignedTo = subtask.assignedTo?.map((item) => plainToClass(SubTaskAssignedTo, item)) || [];
      userIds = subtask.assignedTo?.map((item) => item.userId) || [];

      const subtaskFG = this._fb.group({
        id: [subtask.id],
        title: [subtask.title, [Validators.required]],
        description: [subtask.description, [Validators.required]],
        date: [DateTimeUtility.getDateOnly(subtask.date), [Validators.required]],
        assignedTo: [userIds, [Validators.required]],
        status: [subtask.status, [Validators.required]],
      });
      this.subTaskFormArray.push(subtaskFG);
      subtaskFG.updateValueAndValidity();
      subtaskFG.markAsTouched();
    });

    this.subTaskFormArray.markAsTouched();
    this.subTaskFormArray.markAsDirty();
    this.subTaskFormArray.updateValueAndValidity();
    this.form.markAsTouched();
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
  }

  private _patchForm(task: TaskEntity) {
    this.form.patchValue({
      id: task.id,
      title: task.title,
      description: stripHtml(task.description),
      date: DateTimeUtility.getDateOnly(task.date),
      tags: task.tags,
      status: task.status || '1',
      collaborators: task.collaborators,
      type: task.groupId ? TaskType.Group : TaskType.Individual,
    });
    this.form.get('image').setValue(task?.imageUrl);

    setTimeout(() => {
      this.form.get('groupId').setValue(task.groupId > 0 ? task.groupId : null);
    }, 1000);

    if (task.subtasks && task.subtasks.length > 0) {
      this._patchSubTasks(task.subtasks);
    }

    if (!task.groupId || task.groupId === 0) {
      this._fetchTaskCollaboratorsAndPatch(task);
    }
  }

  private _fetchTaskTags() {
    this.call(this._useTask.getTaskTags()).then((response) => {
      this.tags = this.removeNullObjects(response) as TagEntity[];
    });
  }

  private _fetchTaskCollaboratorsAndPatch(task: TaskEntity) {
    this.call(this._useTask.getTaskCollaboratorsWithIdsOnly(task.id)).then((response) => {
      this.form.get('collaborators').setValue(response);
    });
  }

  private _fetchTeamUsersAndGroups() {
    this.call(this._useTask.getTeamUsersAndGroups()).then((response) => {
      this.groups = this.removeNullObjects(response.groups) as Partial<GroupEntity>[];
      this.users = this.removeNullObjects(response.users) as Partial<UserEntity>[];
    });
  }

  private _fetchGroupParticipants(groupId: number) {
    this._useGroup
      .getApprovedGroupUsers(groupId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (group) => {
          this._groupParticipants = group.participants;
        },
      });
  }

  private _updateData() {
    // My Tasks: Show tasks where current user is a participant (via taskCollaborators)
    this[TasksTab.MyTasks] = this[TasksTab.AllTasks]
      ?.filter((task) => {
        // Check if current user is in the taskCollaborators array
        return task.taskCollaborators?.some((collaborator) => collaborator.userId === this._currentUserId);
      })
      .sort((a, b) => +b.id - +a.id);

    // Group Tasks: Keep the existing logic
    this[TasksTab.GroupTasks] = this[TasksTab.AllTasks]?.filter((task) => task.taskType === TaskType.Group).sort((a, b) => +b.id - +a.id);

    // Assigned Tasks: Tasks created by the current user but assigned to others
    this[TasksTab.AssignedTasks] = this[TasksTab.AllTasks]
      ?.filter((task) => {
        // Task created by current user
        const isCreatedByCurrentUser = task.organizerId === this._currentUserId;

        // But current user is not a participant
        const isCurrentUserNotParticipant = !task.taskCollaborators?.some((collaborator) => collaborator.userId === this._currentUserId);

        return isCreatedByCurrentUser && isCurrentUserNotParticipant;
      })
      .sort((a, b) => +b.id - +a.id);

    // update count in tabs
    this.tabs = this.tabs.map((tab) => {
      tab.count = this[tab.label].length;
      return tab;
    });

    this.isLoading = false;
  }

  private _categorizeTasks(tasks: TaskEntity[]): CategorizedTasksType {
    return tasks?.reduce(
      (acc, task) => {
        switch (task.status) {
          case TaskStatus.ToDo:
            acc[TaskStatus.ToDo].push(task);
            break;
          case TaskStatus.InProgress:
            acc[TaskStatus.InProgress].push(task);
            break;
          case TaskStatus.Completed:
            acc[TaskStatus.Completed].push(task);
            break;
        }
        return acc;
      },
      { [TaskStatus.ToDo]: [], [TaskStatus.InProgress]: [], [TaskStatus.Completed]: [] },
    );
  }

  private _markTasksSectionAsRead() {
    this._useTask.markTasksAsRead().pipe(untilDestroyed(this)).subscribe();
  }

  private _reloadCurrentRoute(path?: string) {
    const currentUrl = path || this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]).then(() => {
        this._bottomSheetService.close();
      });
    });
  }
}
